import { css, cx } from "@emotion/css"
import { useCallback } from "react"

import CircleHeader from "@p-ban/common/src/components/CircleHeader"
import CloseButton from "@p-ban/common/src/components/button/CloseButton/CloseButton"
import NumberField from "@p-ban/common/src/components/form/NumberField/NumberField"
import Spacer from "@p-ban/common/src/components/layout/Spacer"
import useDebounce from "@p-ban/common/src/hooks/useDebounce"

import useEstimateQuery from "../../../../../../api/estimate/useEstimateQuery"
import parseResult from "../../../../service/parseResult"
import type { EstimateParams } from "../../../../types/params"
import type { MassProductionSimulationResultType } from "../../../../types/server/massProduction"
import OneClickErrorText from "../../../components/1Click/OneClickErrorText"
import COLOR_CODE from "../../../constant/color"
import MASS_PRODUCTION_SIMULATION_RESULT from "../../../constant/massProductionSimulationResult"
import Table from "./Table"

const useMassProductionSimulation = (
  params: EstimateParams,
  count: number | null,
  setCount: React.Dispatch<React.SetStateAction<number | null>>,
  repeatManufactureCount: number | null,
  setRepeatManufactureCount: React.Dispatch<React.SetStateAction<number | null>>
) => {
  const onCountChange = useCallback(
    (value: number | null) => {
      setCount(value)
    },
    [setCount]
  )
  const debounce = useDebounce(onCountChange)

  const onRepeatManufactureCountChange = useCallback(
    (value: number | null) => {
      setRepeatManufactureCount(value)
    },
    [setRepeatManufactureCount]
  )
  const debounceRepeat = useDebounce(onRepeatManufactureCountChange)
  const { data, isLoading } = useEstimateQuery({
    ...params,
    values: {
      ...params.values,
      cnt: count,
    },
    options: {
      repCnt: repeatManufactureCount,
    },
  })

  const result = data && !data.isError ? parseResult(data).massProductionSimulation : null
  const errors = data && data.isError ? Object.values(data.messages) : []

  return {
    result,
    onCountChange: debounce,
    onRepeatManufactureCountChange: debounceRepeat,
    repeatManufactureCount,
    isLoading,
    errors,
  }
}

const getMessage = (type?: MassProductionSimulationResultType, repeatOrderCount?: number) => {
  switch (type) {
    case MASS_PRODUCTION_SIMULATION_RESULT.MASS_PRODUCTION_COURSE_IS_CHEAPER:
      return "量産コースがお得です。"
    case MASS_PRODUCTION_SIMULATION_RESULT.MASS_PRODUCTION_COURSE_IS_CHEAPER_IF_REPEAT:
      return (
        <>
          量産コースをご選択頂いた場合、
          <span className={styles.redText}> {repeatOrderCount} </span>
          回目のリピート注文からお得になります。
        </>
      )
    case MASS_PRODUCTION_SIMULATION_RESULT.NORMAL_COURSE_IS_CHEAPER:
      return "ノーマルコースがお得です。"
  }
}

const ModalContent = ({
  params,
  count,
  setCount,
  repeatManufactureCount,
  setRepeatManufactureCount,
  close,
}: {
  params: EstimateParams
  count: number | null
  setCount: React.Dispatch<React.SetStateAction<number | null>>
  repeatManufactureCount: number | null
  setRepeatManufactureCount: React.Dispatch<React.SetStateAction<number | null>>
  close: () => void
}) => {
  const { result, onCountChange, onRepeatManufactureCountChange, isLoading, errors } = useMassProductionSimulation(
    params,
    count,
    setCount,
    repeatManufactureCount,
    setRepeatManufactureCount
  )

  const message = getMessage(result?.type, result?.repeatOrderCount)

  return (
    <div className={styles.container}>
      <div>
        <CircleHeader color={COLOR_CODE.SIMULATION_CIRCLE_BROWN}>量産シミュレーション</CircleHeader>
        <Spacer size={20} />
        <p className={styles.p}>
          ノーマルコースと量産コースの累計金額を比較しています。
          <br />
          リピート製造のご予定がある案件で、どちらのコースを選択されるかの目安としてご利用ください。
        </p>
        <div className={styles.numberFields}>
          <label>
            <NumberField
              min={1}
              max={9999}
              maxDecimalDigits={0}
              step={1}
              prefix="製造枚数"
              suffix="枚"
              value={count}
              onUpdateValue={onCountChange}
            />
          </label>
          <Spacer size={60} axis="horizontal" />
          <label>
            <NumberField
              min={1}
              max={9999}
              maxDecimalDigits={0}
              step={1}
              prefix="リピート製造枚数"
              suffix="枚"
              value={repeatManufactureCount}
              onUpdateValue={onRepeatManufactureCountChange}
            />
          </label>
        </div>
        {errors &&
          errors.map((error, i) => (
            <OneClickErrorText key={i} className={cx(styles.p)}>
              {error}
            </OneClickErrorText>
          ))}
        <Table result={result} isLoading={isLoading} />
        <Spacer size={20} />
        {message && <p className={styles.resultMessage}>{message}</p>}
        <p className={styles.p}>※為替の変動や価格改定などにより、結果が変わる可能性がございます。</p>
      </div>
      <Spacer size={20} />
      <div>
        <CloseButton onClick={close} />
      </div>
    </div>
  )
}

export default ModalContent

const styles = {
  container: css`
    display: flex;
    font-size: 13px;
  `,
  numberFields: css`
    display: flex;
    padding-top: 20px;
    padding-bottom: 20px;
  `,
  redText: css`
    color: ${COLOR_CODE.SIMULATION_RED};
  `,
  p: css`
    line-height: 23px;
  `,
  resultMessage: css`
    font-weight: bold;
    line-height: 23px;
    text-decoration: underline;
  `,
}
